import { Controller } from "@hotwired/stimulus"

/**
 * Marks elements belonging to the current user with a "You" badge.
 *
 * When Turbo Streams broadcast updates to all participants, the server
 * renders a "generic" view without user-specific personalization.
 * This controller adds the "You" badge client-side by comparing the
 * element's user ID with the current user ID stored in the body data attribute.
 *
 * Usage:
 *   <!-- In layout -->
 *   <body data-current-user-id="123">
 *
 *   <!-- In partial -->
 *   <div data-controller="current-user-marker"
 *        data-current-user-marker-user-id-value="123">
 *     <span data-current-user-marker-target="badge" class="hidden">You</span>
 *   </div>
 *
 * The badge target will be shown if the user ID matches the current user.
 *
 * @example Participant row
 *   <div data-controller="current-user-marker"
 *        data-current-user-marker-user-id-value="<%= participant.user.id %>">
 *     <span data-current-user-marker-target="badge"
 *           class="hidden px-3 py-1 bg-primary-2-900/50 text-primary-2-200 text-xs font-semibold rounded-full border border-primary-2-700">
 *       You
 *     </span>
 *   </div>
 */
export default class extends Controller {
  static values = {
    userId: Number
  }

  static targets = ["badge"]

  connect() {
    this.markIfCurrentUser()
  }

  /**
   * Shows the badge if this element belongs to the current user.
   */
  markIfCurrentUser() {
    const currentUserId = document.body.dataset.currentUserId
    
    if (!currentUserId || !this.hasUserIdValue) {
      return
    }

    if (this.userIdValue.toString() === currentUserId) {
      this.showBadge()
    }
  }

  /**
   * Shows the badge target by removing the hidden class.
   */
  showBadge() {
    if (this.hasBadgeTarget) {
      this.badgeTarget.classList.remove("hidden")
    }
  }
}
